import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    Typography,
} from '@mui/material';
import { useState } from 'react';
import LoadingButton from '@/components/Buttons/LoadingButton';
import { resendIntakeEmail } from '@/services/EmailService';
import { errorSnackbar, successSnackbar } from '@/utils/snackbar';

interface Props {
    prospectId: string;
    contactId: string;
    contactName: string;
    bouncedEmail?: string | null;
    mode?: 'send' | 'resend';
    onClose(): void;
}

export default function ResendIntakeDialog({
    prospectId,
    contactId,
    contactName,
    bouncedEmail,
    mode = 'resend',
    onClose,
}: Props) {
    const [isSending, setIsSending] = useState(false);
    const isFirstSend = mode === 'send';

    const handleSend = async () => {
        setIsSending(true);
        try {
            await resendIntakeEmail({
                prospectId,
                contactId,
            });
            successSnackbar(
                isFirstSend ? 'Intake email sent successfully' : 'Intake email resent successfully',
            );
            onClose();
        } catch (error: any) {
            errorSnackbar(
                error?.response?.data ||
                    (isFirstSend ? 'Failed to send intake email' : 'Failed to resend intake email'),
            );
        } finally {
            setIsSending(false);
        }
    };

    return (
        <Dialog open onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>{isFirstSend ? 'Send Intake Email' : 'Resend Intake'}</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ paddingTop: '8px' }}>
                    <Typography variant="body2">
                        {isFirstSend
                            ? `Would you like to send the intake email to ${contactName}?`
                            : `Would you like to resend the intake email to ${contactName}?`}
                    </Typography>
                    {bouncedEmail && (
                        <Alert severity="warning">
                            A previous intake email to {bouncedEmail} bounced. Resending to the same
                            address is unlikely to be delivered, so update the contact's email first
                            if it's incorrect.
                        </Alert>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions sx={{ padding: '16px 24px' }}>
                <Button variant="outlined" onClick={onClose} disabled={isSending}>
                    Cancel
                </Button>
                <LoadingButton variant="contained" onClick={handleSend} loading={isSending}>
                    {isFirstSend ? 'Send' : 'Resend'}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
}
